<template>
  <div class="bdm-wrap">
    <!-- ══ HEADER ══ -->
    <div class="bdm-header">
      <div class="bdm-header-left">
        <div class="bdm-icon-wrap">
          <v-icon size="20" color="white">mdi-map-search</v-icon>
        </div>
        <div>
          <div class="bdm-title">ความหนาแน่นสาขาวิชา — รายจังหวัด</div>
          <div class="bdm-subtitle">สำรวจการกระจายตัวของสาขาวิชาในเชิงพื้นที่ทั่วประเทศ</div>
        </div>
      </div>
      <div class="bdm-header-right">
        <v-chip x-small outlined color="#e2e8f0" class="mr-2" style="color:#64748b">
          <v-icon size="11" left>mdi-calendar</v-icon>ปี {{ year }}
        </v-chip>
        <v-chip x-small color="#3b82f6" dark v-if="selectedBranch">
          <v-icon size="11" left>mdi-book-open-variant</v-icon>{{ selectedBranch }}
        </v-chip>
      </div>
    </div>

    <!-- ══ CONTROLS ══ -->
    <div class="bdm-controls">
      <!-- Search branch -->
      <div class="bdm-search-wrap">
        <div class="bdm-search-label">
          <v-icon size="13" color="#64748b">mdi-magnify</v-icon>
          <span>ค้นหาสาขาวิชา</span>
        </div>
        <div class="bdm-search-input-wrap">
          <input
            v-model="branchQuery"
            class="bdm-input"
            placeholder="เช่น ช่างยนต์, คอมพิวเตอร์..."
            @input="onBranchInput"
            @focus="showDropdown = true"
            @blur="hideDropdownDelay"
            autocomplete="off"
          />
          <button v-if="branchQuery" class="bdm-clear-btn" @click="clearBranch">
            <v-icon size="14" color="#9ca3af">mdi-close-circle</v-icon>
          </button>
        </div>
        <!-- Dropdown suggestions -->
        <div class="bdm-dropdown" v-if="showDropdown && branchSuggestions.length">
          <div
            v-for="b in branchSuggestions" :key="b.name"
            class="bdm-dropdown-item"
            @mousedown.prevent="selectBranch(b)"
          >
            <div class="bdm-drop-name">{{ b.name }}</div>
            <div class="bdm-drop-meta">
              <span class="bdm-drop-type">{{ b.subjectType }}</span>
              <span class="bdm-drop-count">{{ formatNum(b.total) }} คน · {{ b.provinceCount }} จังหวัด</span>
            </div>
          </div>
          <div v-if="!branchSuggestions.length" class="bdm-no-result">ไม่พบสาขาวิชา</div>
        </div>
      </div>

      <!-- Level filter -->
      <div class="bdm-filter-group">
        <div class="bdm-filter-label">ระดับ</div>
        <div class="bdm-pill-group">
          <span
            v-for="lv in levelOptions" :key="lv.value"
            :class="['bdm-pill', { 'bdm-pill--active': levelFilter === lv.value }]"
            @click="levelFilter = lv.value"
          >{{ lv.label }}</span>
        </div>
      </div>

      <!-- Color scheme -->
      <div class="bdm-filter-group">
        <div class="bdm-filter-label">สี</div>
        <div class="bdm-pill-group">
          <span
            v-for="s in schemeOptions" :key="s.value"
            :class="['bdm-pill', { 'bdm-pill--active': colorScheme === s.value }]"
            @click="colorScheme = s.value"
          >
            <span class="bdm-scheme-dot" :style="`background:${s.color}`"></span>
            {{ s.label }}
          </span>
        </div>
      </div>

      <!-- Metric selector -->
      <div class="bdm-filter-group">
        <div class="bdm-filter-label">แสดง</div>
        <div class="bdm-pill-group">
          <span
            v-for="m in metricOptions" :key="m.value"
            :class="['bdm-pill', { 'bdm-pill--active': metric === m.value }]"
            @click="metric = m.value"
          >{{ m.label }}</span>
        </div>
      </div>
    </div>

    <!-- ══ MAIN CONTENT ══ -->
    <div class="bdm-body">
      <!-- LEFT: MAP -->
      <div class="bdm-map-panel">
        <!-- No branch selected state -->
        <div v-if="!selectedBranch" class="bdm-empty-state">
          <div class="bdm-empty-icon">
            <v-icon size="52" color="#cbd5e1">mdi-map-search-outline</v-icon>
          </div>
          <div class="bdm-empty-title">ค้นหาสาขาวิชาเพื่อดูการกระจายบนแผนที่</div>
          <div class="bdm-empty-sub">พิมพ์ชื่อสาขาวิชาในช่องด้านบน เช่น "ช่างยนต์" หรือ "การบัญชี"</div>
          <div class="bdm-quick-chips">
            <span
              v-for="q in quickBranches" :key="q"
              class="bdm-quick-chip"
              @click="quickSelect(q)"
            >{{ q }}</span>
          </div>
          <!-- Show full map faded -->
          <div class="bdm-map-faded">
            <svg viewBox="0 0 550 820" class="bdm-svg-bg" xmlns="http://www.w3.org/2000/svg">
              <g v-for="prov in allProvinces" :key="prov.id">
                <path :d="prov.path" fill="#e2e8f0" stroke="#fff" stroke-width="0.8"/>
              </g>
            </svg>
          </div>
        </div>

        <!-- Map with data -->
        <div v-else class="bdm-map-container" ref="mapContainer">
          <div class="bdm-map-title-bar">
            <span class="bdm-map-branch-name">{{ selectedBranch }}</span>
            <span class="bdm-map-stats">
              รวม {{ formatNum(branchTotal) }} คน · {{ activeProvinceCount }} จังหวัด
            </span>
            <div class="bdm-zoom-btns">
              <button class="bdm-zoom-btn" @click="zoomIn">+</button>
              <button class="bdm-zoom-btn" @click="zoomOut">−</button>
              <button class="bdm-zoom-btn" @click="resetZoom">⊡</button>
            </div>
          </div>

          <!-- SVG Map -->
          <div
            class="bdm-svg-wrap"
            @wheel.prevent="onWheel"
            @mousedown="onPanStart"
            @mousemove="onPanMove"
            @mouseup="onPanEnd"
            @mouseleave="onPanEnd"
            @touchstart.prevent="onTouchStart"
            @touchmove.prevent="onTouchMove"
            @touchend="onTouchEnd"
          >
            <svg
              viewBox="0 0 550 820"
              :style="svgTransformStyle"
              class="bdm-svg"
              xmlns="http://www.w3.org/2000/svg"
            >
              <!-- Province paths -->
              <g v-for="prov in allProvinces" :key="prov.id"
                @mouseenter="onHoverProv(prov, $event)"
                @mouseleave="onLeaveProv"
                @click="onClickProv(prov)"
                class="bdm-prov-group"
              >
                <path
                  :d="prov.path"
                  :fill="getProvFill(prov)"
                  :stroke="selectedProvince === prov.id ? '#1e40af' : '#fff'"
                  :stroke-width="selectedProvince === prov.id ? 1.8 : 0.8"
                  :class="['bdm-prov-path', { 'bdm-prov-path--active': selectedProvince === prov.id }]"
                />
                <!-- Province name label for larger ones -->
                <text
                  v-if="prov.labelX && getProvValue(prov.name) > 0"
                  :x="prov.labelX" :y="prov.labelY"
                  class="bdm-prov-label"
                  :style="`font-size:${prov.labelSize || 6}px`"
                >{{ prov.shortName || prov.name }}</text>
              </g>
            </svg>
          </div>

          <!-- Legend -->
          <div class="bdm-legend">
            <div class="bdm-legend-title">{{ metricLabel }}</div>
            <div class="bdm-legend-bar">
              <div class="bdm-legend-gradient" :style="legendGradient"></div>
              <div class="bdm-legend-ticks">
                <span>0</span>
                <span>{{ formatNum(Math.round(maxProvValue / 2)) }}</span>
                <span>{{ formatNum(maxProvValue) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Tooltip -->
        <div
          class="bdm-tooltip"
          v-if="tooltip.show"
          :style="`left:${tooltip.x}px;top:${tooltip.y}px`"
        >
          <div class="bdm-tt-header">
            <span class="bdm-tt-prov">{{ tooltip.province }}</span>
            <span class="bdm-tt-region" :style="`background:${tooltip.regionColor}`">{{ tooltip.region }}</span>
          </div>
          <div class="bdm-tt-body">
            <div class="bdm-tt-row">
              <span>นักเรียนรวม</span>
              <strong>{{ formatNum(tooltip.total) }} คน</strong>
            </div>
            <div class="bdm-tt-row">
              <span>ปวช.</span>
              <strong class="c-blue">{{ formatNum(tooltip.ch) }}</strong>
            </div>
            <div class="bdm-tt-row">
              <span>ปวส.</span>
              <strong class="c-purple">{{ formatNum(tooltip.hs) }}</strong>
            </div>
            <div class="bdm-tt-row">
              <span>วิทยาลัย</span>
              <strong>{{ tooltip.colleges }} แห่ง</strong>
            </div>
            <div class="bdm-tt-row" v-if="tooltip.rank">
              <span>อันดับ</span>
              <strong class="c-orange">{{ tooltip.rank }} / {{ activeProvinceCount }}</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT: STATS PANEL -->
      <div class="bdm-stats-panel">
        <!-- Summary cards -->
        <div v-if="selectedBranch" class="bdm-stat-cards">
          <div class="bdm-stat-card bdm-stat-card--blue">
            <div class="bdm-stat-val">{{ formatNum(branchTotal) }}</div>
            <div class="bdm-stat-lbl">นักเรียนรวม</div>
          </div>
          <div class="bdm-stat-card bdm-stat-card--teal">
            <div class="bdm-stat-val">{{ activeProvinceCount }}</div>
            <div class="bdm-stat-lbl">จังหวัดที่มีสาขา</div>
          </div>
          <div class="bdm-stat-card bdm-stat-card--purple">
            <div class="bdm-stat-val">{{ branchColleges }}</div>
            <div class="bdm-stat-lbl">วิทยาลัย</div>
          </div>
          <div class="bdm-stat-card bdm-stat-card--orange">
            <div class="bdm-stat-val">{{ coveragePct }}%</div>
            <div class="bdm-stat-lbl">ครอบคลุมประเทศ</div>
          </div>
        </div>

        <!-- Province ranking table -->
        <div class="bdm-rank-panel" v-if="selectedBranch && provinceRanking.length">
          <div class="bdm-rank-header">
            <v-icon size="14" color="#2563eb" class="mr-1">mdi-podium</v-icon>
            <span>อันดับจังหวัด</span>
            <v-spacer />
            <span class="bdm-rank-count">{{ provinceRanking.length }} จังหวัด</span>
          </div>
          <div class="bdm-rank-search">
            <input v-model="rankSearch" class="bdm-input bdm-input--sm" placeholder="กรองจังหวัด..." />
          </div>
          <div class="bdm-rank-list">
            <div
              v-for="(p, i) in filteredRanking" :key="p.name"
              class="bdm-rank-row"
              :class="{ 'bdm-rank-row--selected': selectedProvince === p.id }"
              @click="onClickProvByName(p.name)"
            >
              <div class="bdm-rank-num" :class="getRankClass(i)">{{ i + 1 }}</div>
              <div class="bdm-rank-info">
                <div class="bdm-rank-name">{{ p.name }}</div>
                <div class="bdm-rank-region" :style="`color:${getRegionColor(p.region)}`">
                  {{ p.region }}
                </div>
              </div>
              <div class="bdm-rank-bars">
                <div class="bdm-rank-bar-row">
                  <div class="bdm-rank-bar-bg">
                    <div
                      class="bdm-rank-bar-fill bdm-rank-bar-fill--blue"
                      :style="`width:${maxProvValue ? (p.ch/maxProvValue)*100 : 0}%`"
                    ></div>
                  </div>
                  <span class="bdm-rank-bar-val">{{ formatNum(p.ch) }}</span>
                </div>
                <div class="bdm-rank-bar-row">
                  <div class="bdm-rank-bar-bg">
                    <div
                      class="bdm-rank-bar-fill bdm-rank-bar-fill--purple"
                      :style="`width:${maxProvValue ? (p.hs/maxProvValue)*100 : 0}%`"
                    ></div>
                  </div>
                  <span class="bdm-rank-bar-val">{{ formatNum(p.hs) }}</span>
                </div>
              </div>
              <div class="bdm-rank-total">{{ formatNum(p.total) }}</div>
            </div>
          </div>
        </div>

        <!-- Region breakdown -->
        <div class="bdm-region-panel" v-if="selectedBranch && regionBreakdown.length">
          <div class="bdm-rank-header">
            <v-icon size="14" color="#0891b2" class="mr-1">mdi-map-legend</v-icon>
            <span>สรุปรายภาค</span>
          </div>
          <div class="bdm-region-list">
            <div v-for="r in regionBreakdown" :key="r.name" class="bdm-region-row">
              <div class="bdm-region-dot" :style="`background:${r.color}`"></div>
              <div class="bdm-region-info">
                <div class="bdm-region-name">{{ r.name }}</div>
                <div class="bdm-region-bar-bg">
                  <div class="bdm-region-bar-fill" :style="`width:${r.pct}%;background:${r.color}`"></div>
                </div>
              </div>
              <div class="bdm-region-vals">
                <div class="bdm-region-total">{{ formatNum(r.total) }}</div>
                <div class="bdm-region-pct">{{ r.pct }}%</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Compare branches (เปรียบเทียบ) -->
        <div class="bdm-compare-panel" v-if="compareList.length > 0 || selectedBranch">
          <div class="bdm-rank-header">
            <v-icon size="14" color="#d97706" class="mr-1">mdi-compare</v-icon>
            <span>เปรียบเทียบสาขา</span>
            <v-spacer />
            <button class="bdm-add-btn" @click="addCompare" :disabled="!selectedBranch || compareList.includes(selectedBranch)">
              <v-icon size="12">mdi-plus</v-icon> เพิ่ม
            </button>
          </div>
          <div v-if="compareList.length === 0" class="bdm-compare-empty">
            คลิก "เพิ่ม" เพื่อเปรียบเทียบสาขาวิชาหลายสาขา
          </div>
          <div v-else class="bdm-compare-list">
            <div v-for="(b, i) in compareList" :key="b" class="bdm-compare-row">
              <span class="bdm-compare-dot" :style="`background:${compareColors[i]}`"></span>
              <span class="bdm-compare-name">{{ b }}</span>
              <span class="bdm-compare-val">{{ formatNum(getCompareBranchTotal(b)) }}</span>
              <button class="bdm-compare-remove" @click="removeCompare(b)">
                <v-icon size="11" color="#9ca3af">mdi-close</v-icon>
              </button>
            </div>
          </div>
          <!-- Mini bar chart comparison -->
          <div v-if="compareList.length > 1" class="bdm-compare-chart">
            <apexchart
              type="bar" height="140"
              :options="compareChartOpts"
              :series="compareChartSeries"
            />
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="!selectedBranch" class="bdm-stats-empty">
          <v-icon size="36" color="#cbd5e1">mdi-chart-bar-stacked</v-icon>
          <div>เลือกสาขาวิชาเพื่อดูสถิติ</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const REGION_COLORS = {
  ภาคกลาง: '#2563eb', ภาคเหนือ: '#059669',
  ภาคตะวันออกเฉียงเหนือ: '#d97706', ภาคใต้: '#7c3aed',
  ภาคตะวันออก: '#0891b2', ภาคตะวันตก: '#db2777',
  กรุงเทพมหานคร: '#4f46e5'
}

// จังหวัดและภูมิภาค mapping
const PROVINCE_REGION = {
  'กรุงเทพมหานคร':'กรุงเทพมหานคร',
  'นนทบุรี':'ภาคกลาง','ปทุมธานี':'ภาคกลาง','สมุทรปราการ':'ภาคกลาง',
  'สมุทรสาคร':'ภาคกลาง','สมุทรสงคราม':'ภาคกลาง','นครปฐม':'ภาคกลาง',
  'สุพรรณบุรี':'ภาคกลาง','พระนครศรีอยุธยา':'ภาคกลาง','อ่างทอง':'ภาคกลาง',
  'สิงห์บุรี':'ภาคกลาง','ชัยนาท':'ภาคกลาง','ลพบุรี':'ภาคกลาง',
  'สระบุรี':'ภาคกลาง','นครนายก':'ภาคกลาง',
  'เชียงใหม่':'ภาคเหนือ','เชียงราย':'ภาคเหนือ','ลำปาง':'ภาคเหนือ',
  'ลำพูน':'ภาคเหนือ','แพร่':'ภาคเหนือ','น่าน':'ภาคเหนือ',
  'พะเยา':'ภาคเหนือ','แม่ฮ่องสอน':'ภาคเหนือ','ตาก':'ภาคเหนือ',
  'สุโขทัย':'ภาคเหนือ','พิษณุโลก':'ภาคเหนือ','กำแพงเพชร':'ภาคเหนือ',
  'พิจิตร':'ภาคเหนือ','อุตรดิตถ์':'ภาคเหนือ','นครสวรรค์':'ภาคเหนือ',
  'อุทัยธานี':'ภาคเหนือ',
  'นครราชสีมา':'ภาคตะวันออกเฉียงเหนือ','ขอนแก่น':'ภาคตะวันออกเฉียงเหนือ',
  'อุดรธานี':'ภาคตะวันออกเฉียงเหนือ','หนองคาย':'ภาคตะวันออกเฉียงเหนือ',
  'บึงกาฬ':'ภาคตะวันออกเฉียงเหนือ','สกลนคร':'ภาคตะวันออกเฉียงเหนือ',
  'นครพนม':'ภาคตะวันออกเฉียงเหนือ','มุกดาหาร':'ภาคตะวันออกเฉียงเหนือ',
  'กาฬสินธุ์':'ภาคตะวันออกเฉียงเหนือ','มหาสารคาม':'ภาคตะวันออกเฉียงเหนือ',
  'ร้อยเอ็ด':'ภาคตะวันออกเฉียงเหนือ','ยโสธร':'ภาคตะวันออกเฉียงเหนือ',
  'อุบลราชธานี':'ภาคตะวันออกเฉียงเหนือ','อำนาจเจริญ':'ภาคตะวันออกเฉียงเหนือ',
  'ชัยภูมิ':'ภาคตะวันออกเฉียงเหนือ','บุรีรัมย์':'ภาคตะวันออกเฉียงเหนือ',
  'สุรินทร์':'ภาคตะวันออกเฉียงเหนือ','ศรีสะเกษ':'ภาคตะวันออกเฉียงเหนือ',
  'เลย':'ภาคตะวันออกเฉียงเหนือ','หนองบัวลำภู':'ภาคตะวันออกเฉียงเหนือ',
  'ชลบุรี':'ภาคตะวันออก','ระยอง':'ภาคตะวันออก','จันทบุรี':'ภาคตะวันออก',
  'ตราด':'ภาคตะวันออก','ฉะเชิงเทรา':'ภาคตะวันออก','ปราจีนบุรี':'ภาคตะวันออก',
  'สระแก้ว':'ภาคตะวันออก',
  'กาญจนบุรี':'ภาคตะวันตก','ราชบุรี':'ภาคตะวันตก','เพชรบุรี':'ภาคตะวันตก',
  'ประจวบคีรีขันธ์':'ภาคตะวันตก',
  'นครศรีธรรมราช':'ภาคใต้','สุราษฎร์ธานี':'ภาคใต้','กระบี่':'ภาคใต้',
  'ตรัง':'ภาคใต้','พัทลุง':'ภาคใต้','สงขลา':'ภาคใต้','สตูล':'ภาคใต้',
  'ปัตตานี':'ภาคใต้','ยะลา':'ภาคใต้','นราธิวาส':'ภาคใต้',
  'พังงา':'ภาคใต้','ภูเก็ต':'ภาคใต้','ระนอง':'ภาคใต้','ชุมพร':'ภาคใต้'
}

// ── Province SVG paths (simplified Thailand provinces) ──
// ใช้ simplified path coordinates สำหรับ SVG viewBox 0 0 550 820
// แต่ละ entry: id (province_name ภาษาไทย), path (SVG d attribute), labelX/Y
const PROVINCES_DATA = [
  // กรุงเทพมหานคร
  { id: 'กรุงเทพมหานคร', name: 'กรุงเทพมหานคร', shortName: 'กทม.', labelX: 285, labelY: 503, labelSize: 6,
    path: 'M278,493 L284,489 L292,490 L296,496 L294,504 L287,507 L279,504 Z' },
  // ภาคกลาง
  { id: 'นนทบุรี', name: 'นนทบุรี', shortName: 'นนทบุรี', labelX: 274, labelY: 489,
    path: 'M268,483 L274,479 L281,481 L282,488 L276,492 L269,490 Z' },
  { id: 'ปทุมธานี', name: 'ปทุมธานี', shortName: 'ปทุมธานี', labelX: 284, labelY: 480,
    path: 'M278,474 L287,471 L294,474 L295,482 L286,485 L279,482 Z' },
  { id: 'สมุทรปราการ', name: 'สมุทรปราการ', shortName: 'สปก.', labelX: 295, labelY: 504,
    path: 'M292,498 L300,496 L304,502 L301,509 L293,509 L290,504 Z' },
  { id: 'สมุทรสาคร', name: 'สมุทรสาคร', shortName: 'สค.', labelX: 263, labelY: 516,
    path: 'M255,510 L264,507 L270,512 L268,519 L260,520 L254,516 Z' },
  { id: 'สมุทรสงคราม', name: 'สมุทรสงคราม', shortName: 'สส.', labelX: 248, labelY: 528,
    path: 'M240,522 L249,519 L254,524 L251,531 L243,532 L238,527 Z' },
  { id: 'นครปฐม', name: 'นครปฐม', shortName: 'นฐ.', labelX: 255, labelY: 503,
    path: 'M243,495 L257,492 L265,498 L262,511 L248,514 L240,506 Z' },
  { id: 'สุพรรณบุรี', name: 'สุพรรณบุรี', shortName: 'สพ.', labelX: 238, labelY: 480,
    path: 'M222,465 L243,462 L252,470 L248,490 L233,494 L220,484 Z' },
  { id: 'พระนครศรีอยุธยา', name: 'พระนครศรีอยุธยา', shortName: 'อย.', labelX: 284, labelY: 466,
    path: 'M274,458 L291,455 L299,463 L297,474 L281,476 L271,468 Z' },
  { id: 'อ่างทอง', name: 'อ่างทอง', shortName: 'อท.', labelX: 264, labelY: 459,
    path: 'M257,453 L270,450 L276,457 L272,465 L259,466 L253,458 Z' },
  { id: 'สิงห์บุรี', name: 'สิงห์บุรี', shortName: 'สห.', labelX: 265, labelY: 445,
    path: 'M258,440 L268,437 L273,443 L270,451 L260,452 L255,445 Z' },
  { id: 'ชัยนาท', name: 'ชัยนาท', shortName: 'ชน.', labelX: 252, labelY: 438,
    path: 'M238,430 L256,426 L264,434 L260,445 L243,447 L234,438 Z' },
  { id: 'ลพบุรี', name: 'ลพบุรี', shortName: 'ลบ.', labelX: 288, labelY: 448,
    path: 'M274,436 L298,432 L308,442 L304,460 L285,463 L273,451 Z' },
  { id: 'สระบุรี', name: 'สระบุรี', shortName: 'สบ.', labelX: 302, labelY: 467,
    path: 'M294,459 L311,456 L318,465 L314,477 L298,479 L291,470 Z' },
  { id: 'นครนายก', name: 'นครนายก', shortName: 'นย.', labelX: 317, labelY: 477,
    path: 'M308,470 L323,468 L329,476 L325,485 L310,486 L304,478 Z' },
  // ภาคเหนือ
  { id: 'เชียงใหม่', name: 'เชียงใหม่', shortName: 'ชม.', labelX: 190, labelY: 215, labelSize: 7,
    path: 'M155,180 L195,170 L230,185 L240,220 L225,255 L190,265 L160,250 L142,220 Z' },
  { id: 'เชียงราย', name: 'เชียงราย', shortName: 'ชร.', labelX: 230, labelY: 142, labelSize: 7,
    path: 'M200,110 L248,108 L268,130 L260,160 L230,170 L200,158 L183,135 Z' },
  { id: 'ลำปาง', name: 'ลำปาง', shortName: 'ลป.', labelX: 225, labelY: 248, labelSize: 6,
    path: 'M195,228 L240,222 L252,245 L245,275 L210,282 L190,262 Z' },
  { id: 'ลำพูน', name: 'ลำพูน', shortName: 'ลพ.', labelX: 208, labelY: 265,
    path: 'M197,258 L218,255 L225,268 L218,280 L200,281 L192,268 Z' },
  { id: 'แพร่', name: 'แพร่', shortName: 'พร.', labelX: 252, labelY: 268,
    path: 'M238,255 L262,252 L272,266 L267,285 L245,288 L233,274 Z' },
  { id: 'น่าน', name: 'น่าน', shortName: 'น่าน', labelX: 272, labelY: 248,
    path: 'M258,220 L286,218 L298,240 L292,270 L270,275 L254,256 Z' },
  { id: 'พะเยา', name: 'พะเยา', shortName: 'พย.', labelX: 245, labelY: 205,
    path: 'M230,193 L256,190 L264,208 L256,224 L232,226 L222,210 Z' },
  { id: 'แม่ฮ่องสอน', name: 'แม่ฮ่องสอน', shortName: 'มส.', labelX: 152, labelY: 235,
    path: 'M120,205 L158,200 L172,225 L165,260 L135,268 L112,245 Z' },
  { id: 'ตาก', name: 'ตาก', shortName: 'ตาก', labelX: 174, labelY: 308, labelSize: 7,
    path: 'M148,278 L192,272 L205,300 L196,340 L162,348 L140,320 Z' },
  { id: 'สุโขทัย', name: 'สุโขทัย', shortName: 'สท.', labelX: 224, labelY: 310,
    path: 'M208,298 L236,294 L246,310 L238,330 L214,333 L202,316 Z' },
  { id: 'พิษณุโลก', name: 'พิษณุโลก', shortName: 'พล.', labelX: 257, labelY: 310,
    path: 'M240,294 L274,291 L284,310 L278,334 L250,337 L237,318 Z' },
  { id: 'กำแพงเพชร', name: 'กำแพงเพชร', shortName: 'กพ.', labelX: 210, labelY: 354,
    path: 'M193,336 L224,332 L234,350 L228,374 L203,377 L188,359 Z' },
  { id: 'พิจิตร', name: 'พิจิตร', shortName: 'พจ.', labelX: 251, labelY: 348,
    path: 'M237,334 L264,330 L274,347 L267,368 L242,371 L230,352 Z' },
  { id: 'อุตรดิตถ์', name: 'อุตรดิตถ์', shortName: 'อต.', labelX: 272, labelY: 290,
    path: 'M258,275 L288,272 L298,290 L290,310 L264,312 L252,294 Z' },
  { id: 'นครสวรรค์', name: 'นครสวรรค์', shortName: 'นว.', labelX: 248, labelY: 395,
    path: 'M225,378 L265,374 L278,392 L270,416 L237,419 L220,400 Z' },
  { id: 'อุทัยธานี', name: 'อุทัยธานี', shortName: 'อน.', labelX: 219, labelY: 413,
    path: 'M200,400 L232,396 L240,412 L233,432 L208,434 L195,418 Z' },
  // ภาคตะวันออกเฉียงเหนือ
  { id: 'นครราชสีมา', name: 'นครราชสีมา', shortName: 'นม.', labelX: 355, labelY: 468, labelSize: 7,
    path: 'M322,445 L390,440 L408,462 L400,498 L352,505 L320,482 Z' },
  { id: 'ขอนแก่น', name: 'ขอนแก่น', shortName: 'ขก.', labelX: 357, labelY: 390, labelSize: 7,
    path: 'M323,370 L385,365 L400,385 L394,415 L336,420 L316,400 Z' },
  { id: 'อุดรธานี', name: 'อุดรธานี', shortName: 'อด.', labelX: 354, labelY: 318,
    path: 'M322,300 L382,295 L395,315 L388,342 L328,347 L310,326 Z' },
  { id: 'หนองคาย', name: 'หนองคาย', shortName: 'นค.', labelX: 357, labelY: 272,
    path: 'M325,258 L388,256 L398,272 L390,286 L327,288 L314,274 Z' },
  { id: 'บึงกาฬ', name: 'บึงกาฬ', shortName: 'บก.', labelX: 406, labelY: 268,
    path: 'M392,258 L432,258 L440,272 L432,284 L393,284 Z' },
  { id: 'สกลนคร', name: 'สกลนคร', shortName: 'สน.', labelX: 406, labelY: 308,
    path: 'M386,292 L430,290 L444,308 L436,330 L388,333 L374,316 Z' },
  { id: 'นครพนม', name: 'นครพนม', shortName: 'นพ.', labelX: 432, labelY: 330,
    path: 'M420,318 L449,318 L457,335 L448,350 L421,350 L412,335 Z' },
  { id: 'มุกดาหาร', name: 'มุกดาหาร', shortName: 'มห.', labelX: 439, labelY: 365,
    path: 'M424,353 L452,352 L460,368 L451,382 L424,381 L415,367 Z' },
  { id: 'กาฬสินธุ์', name: 'กาฬสินธุ์', shortName: 'กส.', labelX: 388, labelY: 358,
    path: 'M369,345 L404,342 L414,358 L406,376 L371,378 L359,362 Z' },
  { id: 'มหาสารคาม', name: 'มหาสารคาม', shortName: 'มค.', labelX: 387, labelY: 398,
    path: 'M368,385 L403,382 L413,398 L405,416 L369,418 L357,402 Z' },
  { id: 'ร้อยเอ็ด', name: 'ร้อยเอ็ด', shortName: 'รอ.', labelX: 403, labelY: 420,
    path: 'M384,407 L420,404 L430,420 L422,438 L386,440 L374,424 Z' },
  { id: 'ยโสธร', name: 'ยโสธร', shortName: 'ยส.', labelX: 425, labelY: 442,
    path: 'M407,430 L440,428 L449,443 L441,458 L408,459 L398,444 Z' },
  { id: 'อุบลราชธานี', name: 'อุบลราชธานี', shortName: 'อบ.', labelX: 449, labelY: 466, labelSize: 6,
    path: 'M426,448 L478,445 L494,464 L484,490 L438,494 L418,473 Z' },
  { id: 'อำนาจเจริญ', name: 'อำนาจเจริญ', shortName: 'อจ.', labelX: 432, labelY: 463,
    path: 'M417,454 L445,452 L451,463 L443,474 L418,474 Z' },
  { id: 'ชัยภูมิ', name: 'ชัยภูมิ', shortName: 'ชย.', labelX: 322, labelY: 440,
    path: 'M300,420 L344,416 L356,436 L348,462 L306,465 L290,446 Z' },
  { id: 'บุรีรัมย์', name: 'บุรีรัมย์', shortName: 'บร.', labelX: 374, labelY: 460,
    path: 'M353,446 L400,443 L412,460 L404,480 L356,483 L341,465 Z' },
  { id: 'สุรินทร์', name: 'สุรินทร์', shortName: 'สน.', labelX: 398, labelY: 482,
    path: 'M378,470 L420,467 L432,483 L422,500 L380,502 L366,485 Z' },
  { id: 'ศรีสะเกษ', name: 'ศรีสะเกษ', shortName: 'ศก.', labelX: 426, labelY: 487,
    path: 'M408,475 L448,473 L458,488 L448,504 L409,505 L397,490 Z' },
  { id: 'เลย', name: 'เลย', shortName: 'เลย', labelX: 310, labelY: 348,
    path: 'M290,330 L326,326 L338,344 L330,368 L296,371 L280,353 Z' },
  { id: 'หนองบัวลำภู', name: 'หนองบัวลำภู', shortName: 'นภ.', labelX: 326, labelY: 358,
    path: 'M309,345 L340,342 L350,358 L342,374 L311,376 L299,360 Z' },
  // ภาคตะวันออก
  { id: 'ชลบุรี', name: 'ชลบุรี', shortName: 'ชบ.', labelX: 320, labelY: 496,
    path: 'M310,486 L332,484 L340,496 L334,512 L312,513 L304,498 Z' },
  { id: 'ระยอง', name: 'ระยอง', shortName: 'รย.', labelX: 340, labelY: 508,
    path: 'M328,499 L350,497 L358,509 L350,522 L329,522 L321,510 Z' },
  { id: 'จันทบุรี', name: 'จันทบุรี', shortName: 'จบ.', labelX: 364, labelY: 514,
    path: 'M350,504 L378,502 L388,516 L378,530 L351,530 L342,516 Z' },
  { id: 'ตราด', name: 'ตราด', shortName: 'ตร.', labelX: 393, labelY: 523,
    path: 'M380,514 L404,513 L412,524 L404,534 L380,534 Z' },
  { id: 'ฉะเชิงเทรา', name: 'ฉะเชิงเทรา', shortName: 'ฉช.', labelX: 322, labelY: 476,
    path: 'M308,466 L336,463 L345,476 L338,490 L311,490 L300,477 Z' },
  { id: 'ปราจีนบุรี', name: 'ปราจีนบุรี', shortName: 'ปจ.', labelX: 342, labelY: 468,
    path: 'M330,458 L358,455 L368,468 L360,482 L332,483 L322,470 Z' },
  { id: 'สระแก้ว', name: 'สระแก้ว', shortName: 'สก.', labelX: 358, labelY: 484,
    path: 'M346,474 L372,471 L382,484 L373,498 L347,498 L337,485 Z' },
  // ภาคตะวันตก
  { id: 'กาญจนบุรี', name: 'กาญจนบุรี', shortName: 'กจ.', labelX: 196, labelY: 454, labelSize: 6,
    path: 'M162,418 L218,414 L232,440 L222,480 L188,486 L158,464 Z' },
  { id: 'ราชบุรี', name: 'ราชบุรี', shortName: 'รบ.', labelX: 228, labelY: 506,
    path: 'M212,494 L240,490 L250,505 L242,522 L216,524 L206,508 Z' },
  { id: 'เพชรบุรี', name: 'เพชรบุรี', shortName: 'พบ.', labelX: 218, labelY: 538,
    path: 'M200,524 L232,520 L242,537 L232,556 L204,558 L192,540 Z' },
  { id: 'ประจวบคีรีขันธ์', name: 'ประจวบคีรีขันธ์', shortName: 'ปข.', labelX: 210, labelY: 578,
    path: 'M195,558 L224,555 L232,572 L225,596 L198,598 L188,580 Z' },
  // ภาคใต้
  { id: 'ชุมพร', name: 'ชุมพร', shortName: 'ชพ.', labelX: 225, labelY: 618,
    path: 'M210,602 L238,600 L246,618 L238,636 L212,638 L202,620 Z' },
  { id: 'ระนอง', name: 'ระนอง', shortName: 'รน.', labelX: 194, labelY: 630,
    path: 'M180,618 L204,616 L210,630 L202,644 L178,644 L172,630 Z' },
  { id: 'สุราษฎร์ธานี', name: 'สุราษฎร์ธานี', shortName: 'สฎ.', labelX: 242, labelY: 660, labelSize: 6,
    path: 'M218,636 L264,634 L278,656 L268,684 L228,686 L210,663 Z' },
  { id: 'นครศรีธรรมราช', name: 'นครศรีธรรมราช', shortName: 'นศ.', labelX: 272, labelY: 690, labelSize: 6,
    path: 'M254,672 L294,669 L306,688 L295,712 L258,714 L243,694 Z' },
  { id: 'กระบี่', name: 'กระบี่', shortName: 'กบ.', labelX: 230, labelY: 703,
    path: 'M214,692 L244,690 L252,706 L242,720 L216,720 L206,706 Z' },
  { id: 'พังงา', name: 'พังงา', shortName: 'พง.', labelX: 210, labelY: 672,
    path: 'M196,658 L224,655 L232,670 L222,686 L198,687 L188,671 Z' },
  { id: 'ภูเก็ต', name: 'ภูเก็ต', shortName: 'ภก.', labelX: 202, labelY: 700,
    path: 'M194,690 L212,690 L216,704 L208,716 L194,714 L188,701 Z' },
  { id: 'ตรัง', name: 'ตรัง', shortName: 'ตง.', labelX: 242, labelY: 730,
    path: 'M226,718 L254,716 L262,731 L252,744 L227,744 L218,730 Z' },
  { id: 'พัทลุง', name: 'พัทลุง', shortName: 'พท.', labelX: 264, labelY: 726,
    path: 'M250,716 L276,714 L284,728 L274,742 L250,742 L242,728 Z' },
  { id: 'สงขลา', name: 'สงขลา', shortName: 'สข.', labelX: 286, labelY: 740,
    path: 'M272,728 L300,726 L310,742 L300,760 L274,760 L263,744 Z' },
  { id: 'สตูล', name: 'สตูล', shortName: 'สต.', labelX: 252, labelY: 758,
    path: 'M238,746 L262,744 L268,758 L258,770 L236,769 L229,756 Z' },
  { id: 'ปัตตานี', name: 'ปัตตานี', shortName: 'ปน.', labelX: 310, labelY: 760,
    path: 'M298,750 L322,748 L330,761 L320,773 L298,772 L290,760 Z' },
  { id: 'ยะลา', name: 'ยะลา', shortName: 'ยล.', labelX: 296, labelY: 775,
    path: 'M282,764 L308,762 L316,775 L306,788 L282,787 L274,774 Z' },
  { id: 'นราธิวาส', name: 'นราธิวาส', shortName: 'นธ.', labelX: 324, labelY: 776,
    path: 'M312,765 L336,764 L344,777 L334,790 L312,789 L304,776 Z' }
]

export default {
  name: 'BranchDensityMap',

  props: {
    // studentData: Array of raw rows จาก rate_work_college_admin.php action:list
    studentData: { type: Array, default: () => [] },
    year: { type: String, default: '2568' }
  },

  data () {
    return {
      branchQuery: '',
      showDropdown: false,
      selectedBranch: '',
      levelFilter: 'all',
      colorScheme: 'blue',
      metric: 'total',
      rankSearch: '',
      selectedProvince: null,
      // zoom/pan
      svgScale: 1,
      svgTranslateX: 0,
      svgTranslateY: 0,
      isPanning: false,
      panStart: { x: 0, y: 0, tx: 0, ty: 0 },
      // touch
      lastTouchDist: 0,
      // tooltip
      tooltip: { show: false, x: 0, y: 0, province: '', region: '', regionColor: '', total: 0, ch: 0, hs: 0, colleges: 0, rank: 0 },
      // compare
      compareList: [],
      compareColors: ['#2563eb', '#dc2626', '#059669', '#d97706', '#7c3aed'],
      levelOptions: [
        { label: 'ทั้งหมด', value: 'all' },
        { label: 'ปวช.', value: 'ch' },
        { label: 'ปวส.', value: 'hs' }
      ],
      schemeOptions: [
        { label: 'น้ำเงิน', value: 'blue', color: '#2563eb' },
        { label: 'เขียว', value: 'green', color: '#059669' },
        { label: 'ส้ม', value: 'orange', color: '#ea580c' },
        { label: 'ม่วง', value: 'purple', color: '#7c3aed' }
      ],
      metricOptions: [
        { label: 'จำนวนนักเรียน', value: 'total' },
        { label: 'จำนวนวิทยาลัย', value: 'colleges' }
      ],
      quickBranches: ['ช่างยนต์', 'ช่างไฟฟ้า', 'การบัญชี', 'คอมพิวเตอร์', 'การตลาด', 'ช่างก่อสร้าง']
    }
  },

  computed: {
    allProvinces () { return PROVINCES_DATA },

    // index ข้อมูลนักเรียนตามสาขาและจังหวัด
    branchProvinceIndex () {
      // { branchName: { provinceName: { ch, hs, total, colleges: Set } } }
      const idx = {}
      this.studentData.forEach(r => {
        const branch = r.name_type_course || r.rate_id_course_branch || 'ไม่ระบุ'
        const prov = r.province_name || 'ไม่ระบุ'
        if (!idx[branch]) idx[branch] = {}
        if (!idx[branch][prov]) idx[branch][prov] = { ch: 0, hs: 0, total: 0, colleges: new Set() }
        const v = (+r.rate_work_field_study_lavel_1 || 0) + (+r.rate_work_field_study_lavel_2 || 0) + (+r.rate_work_field_study_lavel_3 || 0)
        if (r.rate_work_college_id_class == 20) idx[branch][prov].ch += v
        else if (r.rate_work_college_id_class == 30) idx[branch][prov].hs += v
        idx[branch][prov].total += v
        idx[branch][prov].colleges.add(r.rate_work_college_code)
      })
      return idx
    },

    allBranchList () {
      const map = {}
      this.studentData.forEach(r => {
        const k = r.name_type_course || r.rate_id_course_branch || 'ไม่ระบุ'
        if (!map[k]) map[k] = { name: k, subjectType: r.subject_typeName || '', total: 0, colleges: new Set(), provinces: new Set() }
        const v = (+r.rate_work_field_study_lavel_1 || 0) + (+r.rate_work_field_study_lavel_2 || 0) + (+r.rate_work_field_study_lavel_3 || 0)
        map[k].total += v
        map[k].colleges.add(r.rate_work_college_code)
        map[k].provinces.add(r.province_name)
      })
      return Object.values(map).map(b => ({ ...b, colleges: b.colleges.size, provinceCount: b.provinces.size }))
        .sort((a, b) => b.total - a.total)
    },

    branchSuggestions () {
      if (!this.branchQuery) return this.allBranchList.slice(0, 12)
      const q = this.branchQuery.toLowerCase()
      return this.allBranchList.filter(b =>
        b.name.toLowerCase().includes(q) || b.subjectType.toLowerCase().includes(q)
      ).slice(0, 15)
    },

    // ข้อมูลสาขาที่เลือก รายจังหวัด
    currentBranchData () {
      if (!this.selectedBranch) return {}
      return this.branchProvinceIndex[this.selectedBranch] || {}
    },

    provinceRanking () {
      if (!this.selectedBranch) return []
      return Object.entries(this.currentBranchData)
        .map(([name, d]) => ({
          name,
          id: name,
          region: PROVINCE_REGION[name] || 'ไม่ระบุ',
          ch: d.ch, hs: d.hs,
          total: this.levelFilter === 'ch' ? d.ch : this.levelFilter === 'hs' ? d.hs : d.total,
          colleges: d.colleges.size
        }))
        .filter(p => p.total > 0)
        .sort((a, b) => b.total - a.total)
    },

    filteredRanking () {
      if (!this.rankSearch) return this.provinceRanking
      const q = this.rankSearch.toLowerCase()
      return this.provinceRanking.filter(p => p.name.toLowerCase().includes(q) || p.region.toLowerCase().includes(q))
    },

    maxProvValue () {
      if (!this.provinceRanking.length) return 1
      return Math.max(...this.provinceRanking.map(p => p.total)) || 1
    },

    activeProvinceCount () { return this.provinceRanking.length },

    branchTotal () {
      return this.provinceRanking.reduce((s, p) => s + p.total, 0)
    },

    branchColleges () {
      const s = new Set()
      Object.values(this.currentBranchData).forEach(d => d.colleges.forEach(c => s.add(c)))
      return s.size
    },

    coveragePct () {
      return Math.round((this.activeProvinceCount / 77) * 100)
    },

    regionBreakdown () {
      const map = {}
      this.provinceRanking.forEach(p => {
        const r = p.region || 'ไม่ระบุ'
        if (!map[r]) map[r] = { name: r, total: 0 }
        map[r].total += p.total
      })
      const grand = this.branchTotal || 1
      return Object.values(map).sort((a, b) => b.total - a.total).map(r => ({
        ...r, pct: Math.round((r.total / grand) * 100),
        color: REGION_COLORS[r.name] || '#6b7280'
      }))
    },

    legendGradient () {
      const schemes = {
        blue:   'linear-gradient(to right, #dbeafe, #2563eb)',
        green:  'linear-gradient(to right, #d1fae5, #059669)',
        orange: 'linear-gradient(to right, #fed7aa, #ea580c)',
        purple: 'linear-gradient(to right, #ede9fe, #7c3aed)'
      }
      return `background:${schemes[this.colorScheme] || schemes.blue};border-radius:4px`
    },

    metricLabel () {
      return this.metric === 'colleges' ? 'จำนวนวิทยาลัย' : 'จำนวนนักเรียน'
    },

    svgTransformStyle () {
      return `transform: translate(${this.svgTranslateX}px, ${this.svgTranslateY}px) scale(${this.svgScale}); transform-origin: center center; cursor: ${this.isPanning ? 'grabbing' : 'grab'}`
    },

    compareChartSeries () {
      if (this.compareList.length < 2) return []
      // top 5 provinces for each compare branch
      const topProvs = new Set()
      this.compareList.forEach(b => {
        const data = this.branchProvinceIndex[b] || {}
        Object.entries(data).sort((a, b) => b[1].total - a[1].total).slice(0, 5).forEach(([p]) => topProvs.add(p))
      })
      const provList = [...topProvs].slice(0, 8)
      return this.compareList.map((b, i) => ({
        name: b,
        data: provList.map(p => {
          const d = (this.branchProvinceIndex[b] || {})[p]
          return d ? d.total : 0
        })
      }))
    },

    compareChartOpts () {
      const topProvs = new Set()
      this.compareList.forEach(b => {
        const data = this.branchProvinceIndex[b] || {}
        Object.entries(data).sort((a, b) => b[1].total - a[1].total).slice(0, 5).forEach(([p]) => topProvs.add(p))
      })
      const provList = [...topProvs].slice(0, 8)
      return {
        chart: { background: 'transparent', toolbar: { show: false } },
        colors: this.compareColors,
        plotOptions: { bar: { columnWidth: '70%', borderRadius: 3 } },
        dataLabels: { enabled: false },
        xaxis: { categories: provList, labels: { style: { fontFamily: 'Sarabun,sans-serif', fontSize: '10px' }, rotate: -30 } },
        yaxis: { labels: { formatter: v => v.toLocaleString('th-TH') } },
        legend: { fontFamily: 'Sarabun,sans-serif', fontSize: '11px' },
        grid: { borderColor: '#e5e7eb' },
        tooltip: { y: { formatter: v => v.toLocaleString('th-TH') + ' คน' } }
      }
    }
  },

  watch: {
    studentData () { /* re-compute automatically */ },
    levelFilter () { this.selectedProvince = null }
  },

  methods: {
    formatNum (n) {
      if (n === null || n === undefined || n === 0) return '0'
      return (+n).toLocaleString('th-TH')
    },

    onBranchInput () {
      this.showDropdown = true
    },

    hideDropdownDelay () {
      setTimeout(() => { this.showDropdown = false }, 200)
    },

    selectBranch (b) {
      this.selectedBranch = b.name
      this.branchQuery = b.name
      this.showDropdown = false
      this.selectedProvince = null
      this.svgScale = 1
      this.svgTranslateX = 0
      this.svgTranslateY = 0
    },

    clearBranch () {
      this.branchQuery = ''
      this.selectedBranch = ''
      this.selectedProvince = null
    },

    quickSelect (name) {
      const found = this.allBranchList.find(b => b.name.includes(name))
      if (found) {
        this.branchQuery = found.name
        this.selectBranch(found)
      } else {
        this.branchQuery = name
        this.selectedBranch = name
      }
    },

    getProvValue (provName) {
      if (!this.selectedBranch) return 0
      const d = this.currentBranchData[provName]
      if (!d) return 0
      if (this.metric === 'colleges') return d.colleges.size
      if (this.levelFilter === 'ch') return d.ch
      if (this.levelFilter === 'hs') return d.hs
      return d.total
    },

    getProvFill (prov) {
      if (!this.selectedBranch) return '#e2e8f0'
      const val = this.getProvValue(prov.name)
      if (val === 0) return '#f1f5f9'
      const pct = val / this.maxProvValue
      return this.interpolateColor(pct)
    },

    interpolateColor (t) {
      // t: 0..1
      const schemes = {
        blue:   { from: [219, 234, 254], to: [37, 99, 235] },
        green:  { from: [209, 250, 229], to: [5, 150, 105] },
        orange: { from: [254, 215, 170], to: [234, 88, 12] },
        purple: { from: [237, 233, 254], to: [124, 58, 237] }
      }
      const s = schemes[this.colorScheme] || schemes.blue
      const r = Math.round(s.from[0] + (s.to[0] - s.from[0]) * t)
      const g = Math.round(s.from[1] + (s.to[1] - s.from[1]) * t)
      const b = Math.round(s.from[2] + (s.to[2] - s.from[2]) * t)
      return `rgb(${r},${g},${b})`
    },

    onHoverProv (prov, event) {
      const d = this.currentBranchData[prov.name]
      if (!this.selectedBranch) return
      const rank = this.provinceRanking.findIndex(p => p.name === prov.name) + 1
      const rect = this.$el.getBoundingClientRect()
      this.tooltip = {
        show: true,
        x: event.clientX - rect.left + 12,
        y: event.clientY - rect.top - 10,
        province: prov.name,
        region: PROVINCE_REGION[prov.name] || '',
        regionColor: REGION_COLORS[PROVINCE_REGION[prov.name]] || '#6b7280',
        total: d ? (this.levelFilter === 'ch' ? d.ch : this.levelFilter === 'hs' ? d.hs : d.total) : 0,
        ch: d ? d.ch : 0,
        hs: d ? d.hs : 0,
        colleges: d ? d.colleges.size : 0,
        rank: rank || null
      }
    },

    onLeaveProv () {
      this.tooltip = { ...this.tooltip, show: false }
    },

    onClickProv (prov) {
      this.selectedProvince = this.selectedProvince === prov.id ? null : prov.id
    },

    onClickProvByName (name) {
      this.selectedProvince = this.selectedProvince === name ? null : name
    },

    getRankClass (i) {
      if (i === 0) return 'bdm-rank-num--gold'
      if (i === 1) return 'bdm-rank-num--silver'
      if (i === 2) return 'bdm-rank-num--bronze'
      return ''
    },

    getRegionColor (name) { return REGION_COLORS[name] || '#6b7280' },

    // ── Zoom / Pan ──
    zoomIn  () { this.svgScale = Math.min(this.svgScale * 1.3, 6) },
    zoomOut () { this.svgScale = Math.max(this.svgScale / 1.3, 0.5) },
    resetZoom () { this.svgScale = 1; this.svgTranslateX = 0; this.svgTranslateY = 0 },

    onWheel (e) {
      const delta = e.deltaY > 0 ? 0.85 : 1.18
      this.svgScale = Math.max(0.5, Math.min(6, this.svgScale * delta))
    },

    onPanStart (e) {
      this.isPanning = true
      this.panStart = { x: e.clientX, y: e.clientY, tx: this.svgTranslateX, ty: this.svgTranslateY }
    },
    onPanMove (e) {
      if (!this.isPanning) return
      this.svgTranslateX = this.panStart.tx + (e.clientX - this.panStart.x)
      this.svgTranslateY = this.panStart.ty + (e.clientY - this.panStart.y)
    },
    onPanEnd () { this.isPanning = false },

    onTouchStart (e) {
      if (e.touches.length === 2) {
        this.lastTouchDist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY)
      } else if (e.touches.length === 1) {
        this.isPanning = true
        this.panStart = { x: e.touches[0].clientX, y: e.touches[0].clientY, tx: this.svgTranslateX, ty: this.svgTranslateY }
      }
    },
    onTouchMove (e) {
      if (e.touches.length === 2) {
        const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY)
        const ratio = dist / (this.lastTouchDist || dist)
        this.svgScale = Math.max(0.5, Math.min(6, this.svgScale * ratio))
        this.lastTouchDist = dist
      } else if (e.touches.length === 1 && this.isPanning) {
        this.svgTranslateX = this.panStart.tx + (e.touches[0].clientX - this.panStart.x)
        this.svgTranslateY = this.panStart.ty + (e.touches[0].clientY - this.panStart.y)
      }
    },
    onTouchEnd () { this.isPanning = false },

    // ── Compare ──
    addCompare () {
      if (this.selectedBranch && !this.compareList.includes(this.selectedBranch) && this.compareList.length < 5) {
        this.compareList.push(this.selectedBranch)
      }
    },
    removeCompare (b) { this.compareList = this.compareList.filter(x => x !== b) },
    getCompareBranchTotal (b) {
      return Object.values(this.branchProvinceIndex[b] || {}).reduce((s, d) => s + d.total, 0)
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700;800&family=IBM+Plex+Mono:wght@400;600&display=swap');

* { box-sizing: border-box; }
.bdm-wrap {
  font-family: 'Sarabun', sans-serif;
  background: #fff; border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,.08);
  overflow: visible; position: relative;
  border: 1px solid #e2e8f0;
}

/* ══ HEADER ══ */
.bdm-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid #f1f5f9;
  background: linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%);
  border-radius: 13px 13px 0 0;
}
.bdm-header-left { display: flex; align-items: center; gap: 12px; }
.bdm-icon-wrap { width: 36px; height: 36px; background: rgba(255,255,255,.2); border-radius: 8px; display: flex; align-items: center; justify-content: center; }
.bdm-title { font-size: 0.95rem; font-weight: 700; color: #fff; }
.bdm-subtitle { font-size: 0.68rem; color: rgba(255,255,255,.7); margin-top: 1px; }
.bdm-header-right { display: flex; align-items: center; gap: 6px; }

/* ══ CONTROLS ══ */
.bdm-controls {
  display: flex; align-items: flex-end; flex-wrap: wrap; gap: 14px;
  padding: 12px 18px; background: #f8fafc; border-bottom: 1px solid #e2e8f0;
}
.bdm-search-wrap { position: relative; min-width: 260px; }
.bdm-search-label { display: flex; align-items: center; gap: 4px; font-size: 0.65rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .3px; margin-bottom: 4px; }
.bdm-search-input-wrap { position: relative; }
.bdm-input {
  width: 100%; padding: 7px 32px 7px 10px; border: 1.5px solid #e2e8f0;
  border-radius: 8px; font-family: 'Sarabun', sans-serif; font-size: 0.82rem;
  outline: none; background: #fff; transition: border-color .15s;
}
.bdm-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.12); }
.bdm-input--sm { padding: 5px 10px; font-size: 0.76rem; }
.bdm-clear-btn { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; padding: 0; }
.bdm-dropdown {
  position: absolute; top: calc(100% + 4px); left: 0; right: 0;
  background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,.12); z-index: 200; max-height: 280px; overflow-y: auto;
}
.bdm-dropdown-item { padding: 9px 12px; cursor: pointer; transition: background .1s; }
.bdm-dropdown-item:hover { background: #eff6ff; }
.bdm-drop-name { font-size: 0.82rem; font-weight: 600; color: #1e293b; }
.bdm-drop-meta { display: flex; align-items: center; gap: 8px; margin-top: 2px; }
.bdm-drop-type { font-size: 0.65rem; color: #6366f1; background: #eef2ff; padding: 1px 6px; border-radius: 4px; }
.bdm-drop-count { font-size: 0.65rem; color: #9ca3af; }
.bdm-no-result { padding: 12px; text-align: center; color: #9ca3af; font-size: 0.8rem; }

.bdm-filter-group { display: flex; flex-direction: column; gap: 4px; }
.bdm-filter-label { font-size: 0.63rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .3px; }
.bdm-pill-group { display: flex; gap: 4px; }
.bdm-pill { padding: 4px 10px; border-radius: 6px; font-size: 0.72rem; cursor: pointer; background: #f1f5f9; color: #64748b; transition: all .15s; display: flex; align-items: center; gap: 5px; font-weight: 500; }
.bdm-pill:hover { background: #e0e7ff; color: #4338ca; }
.bdm-pill--active { background: #2563eb; color: #fff; }
.bdm-scheme-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

/* ══ BODY ══ */
.bdm-body { display: flex; gap: 0; min-height: 600px; }

/* ══ MAP PANEL ══ */
.bdm-map-panel { flex: 1.4; position: relative; min-height: 600px; border-right: 1px solid #f1f5f9; background: #fafcff; overflow: hidden; }

/* Empty state */
.bdm-empty-state { position: relative; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; padding: 30px; text-align: center; }
.bdm-empty-icon { margin-bottom: 12px; }
.bdm-empty-title { font-size: 0.9rem; font-weight: 700; color: #1e293b; margin-bottom: 6px; }
.bdm-empty-sub { font-size: 0.76rem; color: #64748b; max-width: 280px; line-height: 1.5; margin-bottom: 16px; }
.bdm-quick-chips { display: flex; flex-wrap: wrap; gap: 6px; justify-content: center; margin-bottom: 20px; }
.bdm-quick-chip { padding: 5px 12px; background: #eff6ff; color: #2563eb; border-radius: 999px; font-size: 0.75rem; font-weight: 600; cursor: pointer; border: 1px solid #bfdbfe; transition: all .15s; }
.bdm-quick-chip:hover { background: #2563eb; color: #fff; }
.bdm-map-faded { position: absolute; inset: 0; opacity: .2; display: flex; align-items: center; justify-content: center; pointer-events: none; z-index: 0; }
.bdm-svg-bg { width: 100%; max-height: 520px; }

/* Map container */
.bdm-map-container { height: 100%; display: flex; flex-direction: column; }
.bdm-map-title-bar { display: flex; align-items: center; padding: 8px 14px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; gap: 8px; }
.bdm-map-branch-name { font-size: 0.82rem; font-weight: 700; color: #1e293b; flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bdm-map-stats { font-size: 0.68rem; color: #64748b; white-space: nowrap; }
.bdm-zoom-btns { display: flex; gap: 4px; }
.bdm-zoom-btn { width: 24px; height: 24px; border: 1px solid #e2e8f0; background: #fff; border-radius: 5px; font-size: 0.9rem; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #374151; transition: background .1s; }
.bdm-zoom-btn:hover { background: #eff6ff; color: #2563eb; }

.bdm-svg-wrap { flex: 1; overflow: hidden; position: relative; }
.bdm-svg { width: 100%; height: 100%; transition: transform .08s linear; }
.bdm-prov-group { cursor: pointer; }
.bdm-prov-path { transition: opacity .15s, stroke-width .1s; }
.bdm-prov-path:hover { opacity: .82; filter: brightness(1.08); }
.bdm-prov-path--active { filter: brightness(1.15); }
.bdm-prov-label { fill: rgba(30,41,59,.65); text-anchor: middle; dominant-baseline: central; pointer-events: none; font-family: 'Sarabun', sans-serif; }

/* Legend */
.bdm-legend { padding: 8px 14px 10px; border-top: 1px solid #e2e8f0; background: #f8fafc; }
.bdm-legend-title { font-size: 0.65rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .3px; margin-bottom: 5px; }
.bdm-legend-bar { display: flex; flex-direction: column; gap: 3px; }
.bdm-legend-gradient { height: 10px; width: 100%; border-radius: 5px; }
.bdm-legend-ticks { display: flex; justify-content: space-between; font-size: 0.65rem; color: #9ca3af; }

/* Tooltip */
.bdm-tooltip {
  position: absolute; background: #1e293b; color: #fff;
  border-radius: 10px; padding: 10px 14px; font-size: 0.75rem;
  pointer-events: none; z-index: 300; min-width: 160px;
  box-shadow: 0 8px 24px rgba(0,0,0,.25);
}
.bdm-tt-header { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.bdm-tt-prov { font-weight: 700; font-size: 0.82rem; }
.bdm-tt-region { font-size: 0.62rem; padding: 1px 6px; border-radius: 999px; color: #fff; opacity: .9; }
.bdm-tt-body { display: flex; flex-direction: column; gap: 4px; }
.bdm-tt-row { display: flex; justify-content: space-between; gap: 16px; }
.bdm-tt-row span { color: rgba(255,255,255,.65); }
.bdm-tt-row strong { font-weight: 700; }

/* ══ STATS PANEL ══ */
.bdm-stats-panel { width: 310px; flex-shrink: 0; display: flex; flex-direction: column; gap: 0; overflow-y: auto; max-height: 700px; }

/* Summary cards */
.bdm-stat-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; padding: 12px; }
.bdm-stat-card { border-radius: 10px; padding: 10px 12px; }
.bdm-stat-card--blue   { background: #eff6ff; border: 1px solid #bfdbfe; }
.bdm-stat-card--teal   { background: #f0fdfa; border: 1px solid #99f6e4; }
.bdm-stat-card--purple { background: #f5f3ff; border: 1px solid #ddd6fe; }
.bdm-stat-card--orange { background: #fff7ed; border: 1px solid #fed7aa; }
.bdm-stat-val { font-size: 1.3rem; font-weight: 800; color: #1e293b; line-height: 1.1; }
.bdm-stat-lbl { font-size: 0.65rem; color: #64748b; font-weight: 600; margin-top: 2px; }

/* Rank panel */
.bdm-rank-panel, .bdm-region-panel, .bdm-compare-panel { border-top: 1px solid #f1f5f9; }
.bdm-rank-header { display: flex; align-items: center; padding: 9px 14px; font-size: 0.78rem; font-weight: 700; color: #1e293b; background: #f8fafc; border-bottom: 1px solid #f1f5f9; }
.bdm-rank-count { font-size: 0.68rem; color: #64748b; }
.bdm-rank-search { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; }
.bdm-rank-list { max-height: 260px; overflow-y: auto; }
.bdm-rank-row { display: flex; align-items: center; gap: 8px; padding: 7px 12px; cursor: pointer; transition: background .1s; }
.bdm-rank-row:hover { background: #f0f9ff; }
.bdm-rank-row--selected { background: #eff6ff !important; }
.bdm-rank-num { width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.62rem; font-weight: 700; flex-shrink: 0; background: #f1f5f9; color: #64748b; }
.bdm-rank-num--gold   { background: #fef3c7; color: #b45309; }
.bdm-rank-num--silver { background: #f1f5f9; color: #475569; }
.bdm-rank-num--bronze { background: #fef3c7; color: #92400e; }
.bdm-rank-info { flex: 1; min-width: 0; }
.bdm-rank-name   { font-size: 0.76rem; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bdm-rank-region { font-size: 0.63rem; font-weight: 600; }
.bdm-rank-bars { flex: 1.2; display: flex; flex-direction: column; gap: 2px; }
.bdm-rank-bar-row { display: flex; align-items: center; gap: 4px; }
.bdm-rank-bar-bg { flex: 1; height: 4px; background: #e5e7eb; border-radius: 2px; overflow: hidden; }
.bdm-rank-bar-fill { height: 4px; border-radius: 2px; transition: width .4s; }
.bdm-rank-bar-fill--blue   { background: #3b82f6; }
.bdm-rank-bar-fill--purple { background: #8b5cf6; }
.bdm-rank-bar-val { font-size: 0.62rem; color: #64748b; min-width: 34px; text-align: right; }
.bdm-rank-total { font-size: 0.76rem; font-weight: 700; color: #1e293b; min-width: 42px; text-align: right; }

/* Region breakdown */
.bdm-region-list { padding: 8px 12px; display: flex; flex-direction: column; gap: 6px; }
.bdm-region-row { display: flex; align-items: center; gap: 8px; }
.bdm-region-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.bdm-region-info { flex: 1; min-width: 0; }
.bdm-region-name { font-size: 0.73rem; font-weight: 600; color: #1e293b; }
.bdm-region-bar-bg { height: 5px; background: #e5e7eb; border-radius: 3px; overflow: hidden; margin-top: 2px; }
.bdm-region-bar-fill { height: 5px; border-radius: 3px; transition: width .4s; }
.bdm-region-vals { text-align: right; }
.bdm-region-total { font-size: 0.73rem; font-weight: 700; color: #1e293b; }
.bdm-region-pct   { font-size: 0.63rem; color: #9ca3af; }

/* Compare */
.bdm-compare-panel { border-top: 1px solid #f1f5f9; }
.bdm-add-btn { display: flex; align-items: center; gap: 3px; font-size: 0.68rem; background: #eff6ff; color: #2563eb; border: 1px solid #bfdbfe; border-radius: 6px; padding: 2px 8px; cursor: pointer; font-family: 'Sarabun', sans-serif; }
.bdm-add-btn:disabled { opacity: .4; cursor: not-allowed; }
.bdm-compare-empty { padding: 10px 14px; font-size: 0.73rem; color: #9ca3af; }
.bdm-compare-list { padding: 6px 12px; display: flex; flex-direction: column; gap: 4px; }
.bdm-compare-row { display: flex; align-items: center; gap: 7px; font-size: 0.75rem; }
.bdm-compare-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.bdm-compare-name { flex: 1; color: #1e293b; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bdm-compare-val { font-size: 0.73rem; color: #64748b; min-width: 50px; text-align: right; }
.bdm-compare-remove { background: none; border: none; cursor: pointer; padding: 0; }
.bdm-compare-chart { padding: 0 8px 10px; }

/* Stats empty */
.bdm-stats-empty { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: #9ca3af; font-size: 0.8rem; padding: 40px 20px; }

/* Colors */
.c-blue   { color: #2563eb !important; }
.c-purple { color: #7c3aed !important; }
.c-orange { color: #ea580c !important; }

/* Responsive */
@media (max-width: 820px) {
  .bdm-body { flex-direction: column; }
  .bdm-stats-panel { width: 100%; max-height: none; border-top: 1px solid #e2e8f0; }
  .bdm-map-panel { min-height: 420px; }
}
@media (max-width: 560px) {
  .bdm-controls { gap: 10px; }
  .bdm-search-wrap { min-width: 100%; }
}
</style>